import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import type { SchedulingConnection } from '../types/scheduling.types';

interface CalendarEditorModalProps {
  connection: SchedulingConnection | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (connectionId: string, calendarIds: string[]) => Promise<{ ok: boolean; message?: string }>;
  onRevoke: (connectionId: string) => Promise<void>;
  isSaving?: boolean;
}

export function CalendarEditorModal({
  connection,
  open,
  onOpenChange,
  onSave,
  onRevoke,
  isSaving = false,
}: CalendarEditorModalProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);

  useEffect(() => {
    if (!connection) {
      setSelectedIds([]);
      setErrorMessage(null);
      setShowRevokeConfirm(false);
      return;
    }

    setSelectedIds(
      connection.calendars
        .filter((calendar) => calendar.is_included)
        .map((calendar) => calendar.calendar_id)
    );
    setErrorMessage(null);
    setShowRevokeConfirm(false);
  }, [connection]);

  const toggleCalendar = (calendarId: string) => {
    setSelectedIds((current) => (
      current.includes(calendarId)
        ? current.filter((id) => id !== calendarId)
        : [...current, calendarId]
    ));
  };

  const handleSave = async() => {
    if (!connection) {
      return;
    }

    setErrorMessage(null);
    const result = await onSave(connection.id, selectedIds);
    if (result.ok) {
      onOpenChange(false);
      return;
    }
    setErrorMessage(result.message ?? 'Could not save calendar selection.');
  };

  const handleRevoke = async() => {
    if (!connection) {
      return;
    }

    setIsRevoking(true);
    try {
      await onRevoke(connection.id);
      onOpenChange(false);
    } finally {
      setIsRevoking(false);
      setShowRevokeConfirm(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!isSaving && !isRevoking) {
          onOpenChange(nextOpen);
        }
      }}
    >
      <DialogContent className="flex max-h-[85vh] w-[70vw] max-w-[70vw] flex-col overflow-hidden bg-white">
        <DialogHeader>
          <DialogTitle>
            Calendar selection
          </DialogTitle>
          <DialogDescription>
            {connection
              ? `Choose which calendars count toward availability for ${connection.name}.`
              : null}
          </DialogDescription>
        </DialogHeader>

        {connection ? (
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overflow-x-hidden pr-1">
            {connection.calendars.length === 0 ? (
              <p className={cn(portalDs.caption, 'text-[#5E6278]')}>
                No calendars are available for this connection yet.
              </p>
            ) : (
              connection.calendars.map((calendar) => (
                <label
                  key={calendar.id}
                  className="flex cursor-pointer items-start gap-3 rounded-lg border border-[#EDEEF2] px-3 py-3 hover:bg-[#F7F8FA]"
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 size-4 shrink-0 rounded border-[#D9DBE3]"
                    checked={selectedIds.includes(calendar.calendar_id)}
                    onChange={() => toggleCalendar(calendar.calendar_id)}
                    disabled={isSaving || isRevoking}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block break-words text-[13px] font-medium text-[#0D0F1A]">
                      {calendar.calendar_name}
                    </span>
                    <span className={cn(portalDs.caption, 'mt-0.5 block break-all')}>
                      {calendar.calendar_id}
                    </span>
                  </span>
                </label>
              ))
            )}

            {errorMessage ? (
              <p className={cn(portalDs.caption, 'text-[#DC2626]')}>
                {errorMessage}
              </p>
            ) : null}
          </div>
        ) : null}

        <DialogFooter className="flex-col gap-3 sm:flex-row sm:justify-between">
          <div className="min-w-0">
            {showRevokeConfirm ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className={cn(portalDs.caption, 'text-[#5E6278]')}>
                  Remove this member from the pool?
                </span>
                <button
                  type="button"
                  disabled={isRevoking || isSaving}
                  onClick={() => void handleRevoke()}
                  className="inline-flex h-9 items-center justify-center rounded-lg bg-[#DC2626] px-3 text-[13px] font-semibold text-white transition-colors hover:bg-[#B91C1C] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isRevoking ? 'Removing…' : 'Remove'}
                </button>
                <button
                  type="button"
                  disabled={isRevoking || isSaving}
                  onClick={() => setShowRevokeConfirm(false)}
                  className="inline-flex h-9 items-center justify-center rounded-lg border border-[#D9DBE3] px-3 text-[13px] font-semibold text-[#0D0F1A]"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                disabled={!connection?.is_active || isSaving || isRevoking}
                onClick={() => setShowRevokeConfirm(true)}
                className="text-[13px] font-semibold text-[#DC2626] transition-colors hover:text-[#B91C1C] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Remove from pool
              </button>
            )}
          </div>

          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              disabled={isSaving || isRevoking}
              onClick={() => onOpenChange(false)}
              className="inline-flex h-9 items-center justify-center rounded-lg border border-[#D9DBE3] px-4 text-[13px] font-semibold text-[#0D0F1A] transition-colors hover:bg-[#F7F8FA] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!connection || isSaving || isRevoking}
              onClick={() => void handleSave()}
              className="inline-flex h-9 items-center justify-center rounded-lg bg-[#0D0F1A] px-4 text-[13px] font-semibold text-white transition-colors hover:bg-[#2A2D3E] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
                  Saving…
                </>
              ) : 'Save selection'}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
